import {
    ChangeNotificationPolicyModeSchema,
    CreateNotificationPolicySchema,
    DeleteNotificationPolicyDraftSchema,
    InspectNotificationPolicyAudienceSchema,
    LivePilotNotificationPolicySchema,
    NotificationCatalogQuerySchema,
    NotificationDispatchListQuerySchema,
    NotificationManagementEventListQuerySchema,
    NotificationManagementEventWorkspaceParamsSchema,
    NotificationRecordSourceParamsSchema,
    NotificationRecordSourceQuerySchema,
    PreviewDeliverNotificationPolicySchema,
    PublishNotificationPolicySchema,
    RollbackNotificationPolicySchema,
    SimulateNotificationPolicySchema,
    TestDeliverNotificationPolicySchema,
    ToggleNotificationPolicySchema,
    UpdateNotificationPolicyDraftSchema,
    NotificationRecoveryParamsSchema,
    RecoverNotificationDeliverySchema,
    ValidateNotificationPolicySchema,
} from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';

export class NotificationRoutingService {
    constructor(api) { this.api = api; }

    catalog(params) {
        if (params == null) return this.#call(() => this.api.catalog());
        return this.#validated(NotificationCatalogQuerySchema, params, (parsed) => this.api.catalog(parsed));
    }
    listManagementEvents(params = {}) {
        return this.#validated(NotificationManagementEventListQuerySchema, params, (parsed) => this.api.listManagementEvents(parsed));
    }
    managementEventWorkspace(eventName) {
        return this.#validated(
            NotificationManagementEventWorkspaceParamsSchema,
            { eventName },
            (parsed) => this.api.managementEventWorkspace(parsed.eventName),
        );
    }
    listRecordSources(eventName, params = {}) {
        return this.#validated(NotificationRecordSourceParamsSchema, { eventName }, (parsedParams) => (
            this.#validated(NotificationRecordSourceQuerySchema, params, (parsedQuery) => (
                this.api.listRecordSources(parsedParams.eventName, parsedQuery)
            ))
        ));
    }
    listPolicies(params = {}) { return this.#call(() => this.api.listPolicies(params)); }
    getPolicy(id) { return this.#call(() => this.api.getPolicy(id)); }
    createPolicy(data) { return this.#validated(CreateNotificationPolicySchema, data, (parsed) => this.api.createPolicy(parsed)); }
    updateDraft(id, data) {
        return this.#validated(UpdateNotificationPolicyDraftSchema, data, (parsed) => this.api.updateDraft(id, parsed));
    }
    deleteDraft(id, data) {
        return this.#validated(DeleteNotificationPolicyDraftSchema, data, (parsed) => this.api.deleteDraft(id, parsed));
    }
    validateDraft(id, data) {
        return this.#validated(ValidateNotificationPolicySchema, data, (parsed) => this.api.validateDraft(id, parsed));
    }
    audienceInspection(id, data) {
        return this.#validated(
            InspectNotificationPolicyAudienceSchema,
            data,
            (parsed) => this.api.audienceInspection(id, parsed),
        );
    }
    simulate(id, data) {
        return this.#validated(SimulateNotificationPolicySchema, data, (parsed) => this.api.simulate(id, parsed));
    }
    getVerification(id, verificationId) { return this.#call(() => this.api.getVerification(id, verificationId)); }
    verificationRecipients(id, verificationId) {
        return this.#call(() => this.api.verificationRecipients(id, verificationId));
    }
    previewDeliver(id, data) {
        return this.#validated(PreviewDeliverNotificationPolicySchema, data, (parsed) => this.api.previewDeliver(id, parsed));
    }
    livePilot(id, data) {
        return this.#validated(LivePilotNotificationPolicySchema, data, (parsed) => this.api.livePilot(id, parsed));
    }
    testDeliver(id, data) {
        return this.#validated(TestDeliverNotificationPolicySchema, data, (parsed) => this.api.testDeliver(id, parsed));
    }
    publish(id, data) {
        return this.#validated(PublishNotificationPolicySchema, data, (parsed) => this.api.publish(id, parsed));
    }
    rollback(id, data) {
        return this.#validated(RollbackNotificationPolicySchema, data, (parsed) => this.api.rollback(id, parsed));
    }
    changeMode(id, data) {
        return this.#validated(ChangeNotificationPolicyModeSchema, data, (parsed) => this.api.changeMode(id, parsed));
    }
    enable(id, data) {
        return this.#validated(ToggleNotificationPolicySchema, data, (parsed) => this.api.enable(id, parsed));
    }
    disable(id, data) {
        return this.#validated(ToggleNotificationPolicySchema, data, (parsed) => this.api.disable(id, parsed));
    }
    effective(params = {}) { return this.#call(() => this.api.effective(params)); }
    listDispatches(params = {}) {
        return this.#validated(NotificationDispatchListQuerySchema, params, (parsed) => this.api.listDispatches(parsed));
    }
    getDispatch(id) { return this.#call(() => this.api.getDispatch(id)); }
    explainDispatch(id) { return this.#call(() => this.api.explainDispatch(id)); }
    recoverDelivery(dispatchId, intentId, data) {
        return this.#validated(NotificationRecoveryParamsSchema, { dispatchId, intentId }, (params) => (
            this.#validated(RecoverNotificationDeliverySchema, data, (parsed) => (
                this.api.recoverDelivery(params.dispatchId, params.intentId, parsed)
            ))
        ));
    }
    previewResult(id) { return this.#call(() => this.api.previewResult(id)); }
    livePilotResult(id) { return this.#call(() => this.api.livePilotResult(id)); }
    testResult(id) { return this.#call(() => this.api.testResult(id)); }
    exportRecipients(id) { return this.#call(() => this.api.exportRecipients(id)); }

    async #validated(schema, data, operation) {
        const parsed = schema.safeParse(data);
        if (!parsed.success) {
            throw new ApiError(parsed.error.issues[0].message, 400, 'VALIDATION_ERROR');
        }
        return this.#call(() => operation(parsed.data));
    }

    async #call(operation) {
        try {
            const response = await operation();
            return {
                success: response.success ?? true,
                data: response.data ?? response,
                message: response.message,
            };
        } catch (error) {
            if (error instanceof ApiError) throw error;
            if (error.statusCode || error.status) throw ApiError.fromResponse(error);
            throw new ApiError(error.message || 'Notification routing request failed', 500, 'UNKNOWN_ERROR', error);
        }
    }
}
